import type { InvoiceListItemDTO } from '@kanbu/schema/contracts';
import type { Currency } from '@kanbu/schema/enums';
import { formatUtils } from '@kanbu/shared';
import { Trans, useLingui } from '@lingui/react/macro';
import { Button, Dropdown, toast } from '@utima/ui';
import {
  Ban,
  Check,
  Copy,
  ExternalLink,
  MoreHorizontal,
  RefreshCw,
  RotateCcw,
} from 'lucide-react';
import { useState } from 'react';

import { Card } from '@/components/card/Card';

import { ReissueInvoiceDialog } from './ReissueInvoiceDialog';
import { StatusBadge } from './StatusBadge';

type InvoicesCardProps = {
  invoices?: InvoiceListItemDTO[];
  isLoading: boolean;
  onMarkPaid?: (invoiceId: string) => void;
  isMarkingPaid?: boolean;
  markingInvoiceId?: string;
  onVoid?: (invoiceId: string) => void;
  onVoidAndReissue?: (invoiceId: string, duzpDate: string) => void;
  onRefreshLink?: (invoiceId: string) => void;
  pendingInvoiceId?: string;
  /**
   * When false (partner billing), destructive/SuperAdmin actions (mark paid,
   * void, void & reissue, refresh link) are hidden — only View + Copy link
   * remain. Defaults to true (SuperAdmin).
   */
  canManageInvoices?: boolean;
};

function statusTone(
  status: string,
): 'default' | 'success' | 'warning' | 'danger' | 'info' {
  switch (status) {
    case 'paid':
      return 'success';
    case 'open':
      return 'warning';
    case 'void':
    case 'uncollectible':
      return 'danger';

    default:
      return 'default';
  }
}

function dunningLabel(
  status: string,
  days: number,
): {
  label: string;
  tone: 'default' | 'success' | 'warning' | 'danger';
} {
  if (status !== 'open') {
    return { label: '—', tone: 'default' };
  }

  if (days === 0) {
    return { label: 'On time', tone: 'success' };
  }

  if (days <= 6) {
    return { label: `+${days}d reminder`, tone: 'warning' };
  }

  if (days <= 13) {
    return { label: `+${days}d escalation`, tone: 'warning' };
  }

  return { label: `+${days}d cancel due`, tone: 'danger' };
}

export function InvoicesCard({
  invoices,
  isLoading,
  onMarkPaid,
  isMarkingPaid,
  markingInvoiceId,
  onVoid,
  onVoidAndReissue,
  onRefreshLink,
  pendingInvoiceId,
  canManageInvoices = true,
}: InvoicesCardProps) {
  const { t } = useLingui();

  const [reissueInvoice, setReissueInvoice] =
    useState<InvoiceListItemDTO | null>(null);

  const copyLink = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success(t`Invoice link copied.`);
    } catch {
      toast.error(t`Could not copy link.`);
    }
  };

  return (
    <Card title={t`Invoices`} className='mb-6'>
      {isLoading && (
        <p className='text-muted-foreground text-sm'>
          <Trans>Loading invoices...</Trans>
        </p>
      )}

      {!isLoading && (!invoices || invoices.length === 0) && (
        <p className='text-muted-foreground text-sm'>
          <Trans>No invoices found.</Trans>
        </p>
      )}

      {!isLoading && !!invoices?.length && (
        <div className='overflow-x-auto'>
          <table className='w-full min-w-[720px] text-left text-sm'>
            <thead>
              <tr className='border-b'>
                <th className='py-2 pr-2'>
                  <Trans>Invoice</Trans>
                </th>
                <th className='py-2 pr-2'>
                  <Trans>Amount</Trans>
                </th>
                <th className='py-2 pr-2'>
                  <Trans>Due</Trans>
                </th>
                <th className='py-2 pr-2'>
                  <Trans>Status</Trans>
                </th>
                <th className='py-2 pr-2'>
                  <Trans>Dunning</Trans>
                </th>
                <th className='py-2'>
                  <Trans>Actions</Trans>
                </th>
              </tr>
            </thead>
            <tbody>
              {invoices.map(invoice => {
                const dunning = dunningLabel(
                  invoice.status,
                  invoice.daysOverdue,
                );
                const isPending = pendingInvoiceId === invoice.invoiceId;

                return (
                  <tr
                    key={invoice.invoiceId}
                    className={`border-b last:border-b-0 ${
                      invoice.status === 'void'
                        ? 'text-muted-foreground line-through'
                        : ''
                    }`}
                  >
                    <td className='py-2 pr-2'>
                      <span className='flex items-center gap-1'>
                        {invoice.number || invoice.invoiceId}
                        {invoice.isReissue && (
                          <StatusBadge label={t`Reissue`} tone='info' />
                        )}
                      </span>
                    </td>
                    <td className='py-2 pr-2'>
                      {formatUtils.currency(
                        invoice.status === 'paid'
                          ? invoice.amountPaid
                          : invoice.amountDue,
                        invoice.currency as Currency,
                      )}
                    </td>
                    <td className='py-2 pr-2'>
                      {invoice.dueDate
                        ? new Date(invoice.dueDate).toLocaleDateString()
                        : '—'}
                    </td>
                    <td className='py-2 pr-2'>
                      <StatusBadge
                        label={invoice.status}
                        tone={statusTone(invoice.status)}
                      />
                    </td>
                    <td className='py-2 pr-2'>
                      <StatusBadge label={dunning.label} tone={dunning.tone} />
                    </td>
                    <td className='py-2'>
                      <div className='flex items-center gap-1'>
                        {invoice.hostedInvoiceUrl && (
                          <Button
                            size='xs'
                            variant='ghost'
                            onClick={() => {
                              if (invoice.hostedInvoiceUrl) {
                                window.open(
                                  invoice.hostedInvoiceUrl,
                                  '_blank',
                                  'noopener,noreferrer',
                                );
                              }
                            }}
                          >
                            <ExternalLink className='mr-1 size-3.5' />
                            <Trans>View</Trans>
                          </Button>
                        )}

                        {canManageInvoices &&
                          onMarkPaid &&
                          invoice.status === 'open' && (
                            <Button
                              size='xs'
                              variant='ghost'
                              className='text-emerald-600 hover:bg-emerald-50 hover:text-emerald-700'
                              loading={
                                isMarkingPaid &&
                                markingInvoiceId === invoice.invoiceId
                              }
                              onClick={() => onMarkPaid(invoice.invoiceId)}
                            >
                              <Check className='mr-1 size-3.5' />
                              <Trans>Mark paid</Trans>
                            </Button>
                          )}

                        {(invoice.hostedInvoiceUrl ||
                          ((canManageInvoices || onRefreshLink) &&
                            invoice.status === 'open')) && (
                          <Dropdown.Root>
                            <Dropdown.Trigger asChild>
                              <Button
                                size='icon-xs'
                                variant='ghost'
                                aria-label={t`More actions`}
                                loading={isPending}
                              >
                                <MoreHorizontal className='size-4' />
                              </Button>
                            </Dropdown.Trigger>
                            <Dropdown.Content align='end' className='min-w-44'>
                              {invoice.hostedInvoiceUrl && (
                                <Dropdown.Item
                                  className='flex items-center gap-2'
                                  onClick={() =>
                                    copyLink(invoice.hostedInvoiceUrl ?? '')
                                  }
                                >
                                  <Copy className='size-4' />
                                  <Trans>Copy link</Trans>
                                </Dropdown.Item>
                              )}
                              {/* Refreshing a link is non-destructive, so it is
                                  available to partners independently of the
                                  invoice-management capability. */}
                              {onRefreshLink && invoice.status === 'open' && (
                                <Dropdown.Item
                                  className='flex items-center gap-2'
                                  onClick={() =>
                                    onRefreshLink(invoice.invoiceId)
                                  }
                                >
                                  <RefreshCw className='size-4' />
                                  <Trans>Refresh link</Trans>
                                </Dropdown.Item>
                              )}
                              {canManageInvoices &&
                                invoice.status === 'open' && (
                                  <>
                                    {onVoidAndReissue && (
                                      <Dropdown.Item
                                        className='flex items-center gap-2'
                                        onClick={() =>
                                          setReissueInvoice(invoice)
                                        }
                                      >
                                        <RotateCcw className='size-4' />
                                        <Trans>Void & reissue</Trans>
                                      </Dropdown.Item>
                                    )}
                                    {onVoid && (
                                      <>
                                        <Dropdown.Separator />
                                        <Dropdown.Item
                                          className='flex items-center gap-2 text-red-600'
                                          onClick={() =>
                                            onVoid(invoice.invoiceId)
                                          }
                                        >
                                          <Ban className='size-4' />
                                          <Trans>Void</Trans>
                                        </Dropdown.Item>
                                      </>
                                    )}
                                  </>
                                )}
                            </Dropdown.Content>
                          </Dropdown.Root>
                        )}
                      </div>
                      {invoice.hostedInvoiceUrlExpiresAt && (
                        <p className='text-muted-foreground mt-1 text-xs'>
                          <Trans>
                            Link expires{' '}
                            {new Date(
                              invoice.hostedInvoiceUrlExpiresAt,
                            ).toLocaleDateString()}
                          </Trans>
                        </p>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <ReissueInvoiceDialog
        invoice={reissueInvoice}
        isOpen={reissueInvoice !== null}
        isPending={
          !!reissueInvoice && pendingInvoiceId === reissueInvoice.invoiceId
        }
        onClose={() => setReissueInvoice(null)}
        onConfirm={duzpDate => {
          if (reissueInvoice) {
            onVoidAndReissue?.(reissueInvoice.invoiceId, duzpDate);
            setReissueInvoice(null);
          }
        }}
      />
    </Card>
  );
}
